import { useMemo, useReducer } from "react";

interface FormAction<T> {
  type: "SET_FIELD" | "SET_MULTIPLE" | "RESET" | "SET_SUBMITTING" | "SET_ERRORS";
  field?: keyof T;
  value?: T[keyof T];
  updates?: Partial<T>;
  initialState?: T;
  isSubmitting?: boolean;
  errors?: Partial<Record<keyof T, string>>;
}

interface FormMeta<T> {
  isSubmitting: boolean;
  errors: Partial<Record<keyof T, string>>;
}

interface FormState<T> {
  values: T;
  isSubmitting: boolean;
  errors: Partial<Record<keyof T, string>>;
}

function formReducer<T>(state: FormState<T>, action: FormAction<T>): FormState<T> {
  switch (action.type) {
    case "SET_FIELD":
      if (action.field === undefined || action.value === undefined) {
        return state;
      }
      return {
        ...state,
        values: { ...state.values, [action.field]: action.value }
      };
    case "SET_MULTIPLE":
      return {
        ...state,
        values: { ...state.values, ...(action.updates ?? {}) }
      };
    case "RESET":
      return {
        values: action.initialState ?? state.values,
        isSubmitting: false,
        errors: {}
      };
    case "SET_SUBMITTING":
      return {
        ...state,
        isSubmitting: Boolean(action.isSubmitting)
      };
    case "SET_ERRORS":
      return {
        ...state,
        errors: action.errors ?? {}
      };
    default:
      return state;
  }
}

export function useFormState<T>(initialState: T) {
  const [state, dispatch] = useReducer(formReducer<T>, {
    values: initialState,
    isSubmitting: false,
    errors: {}
  });

  const actions = useMemo(() => ({
    set: (field: keyof T, value: T[keyof T]) =>
      dispatch({ type: "SET_FIELD", field, value }),
    setMultiple: (updates: Partial<T>) =>
      dispatch({ type: "SET_MULTIPLE", updates }),
    setSubmitting: (isSubmitting: boolean) =>
      dispatch({ type: "SET_SUBMITTING", isSubmitting }),
    setErrors: (errors: Partial<Record<keyof T, string>>) =>
      dispatch({ type: "SET_ERRORS", errors }),
    reset: () => dispatch({ type: "RESET", initialState })
  }), [initialState]);

  const meta: FormMeta<T> = useMemo(() => ({
    isSubmitting: state.isSubmitting,
    errors: state.errors
  }), [state.errors, state.isSubmitting]);

  return [state.values, actions, meta] as const;
}
